import type {
  AttendanceStatusValue,
  Event,
  EventGuest,
  EventRsvpQuestion,
  EventTag,
  PendingCohostInvite,
} from '@/models/event';
import { AttendanceStatus, type QuestionnaireAnswer } from '@/models/event';

import { mapRsvpQuestion } from './eventRsvpQuestions';

interface WireGuest {
  user_id: string;
  name: string;
  status: string;
  phone?: string | null;
  photo_url?: string;
  has_plus_one?: boolean;
  attendance?: string;
  plus_one_attendance?: string;
  is_member?: boolean;
  paid_confirmed?: boolean;
  payment_verified?: boolean;
  questionnaire_responses?: Record<string, { label?: string; answer?: QuestionnaireAnswer }>;
}

export interface WireEvent {
  id: string;
  slug?: string;
  title: string;
  description?: string;
  start_datetime?: string | null;
  end_datetime?: string | null;

  location?: string;
  latitude?: number | null;
  longitude?: number | null;

  whatsapp_link?: string;
  partiful_link?: string;
  other_link?: string;
  venmo_link?: string;
  cashapp_link?: string;
  zelle_info?: string;
  price?: string;

  rsvp_enabled?: boolean;
  allow_plus_ones?: boolean;
  max_attendees?: number | null;
  attending_count?: number;
  waitlisted_count?: number;
  invited_count?: number;
  guest_rsvp_count?: number;

  datetime_tbd?: boolean;
  has_poll?: boolean;

  created_by_id?: string | null;
  created_by_name?: string | null;
  created_by_photo_url?: string;
  co_host_ids?: string[];
  co_host_names?: string[];
  co_host_photo_urls?: string[];

  guests?: WireGuest[];
  my_rsvp?: string | null;
  my_paid_confirmed?: boolean;
  my_questionnaire_responses?: Record<string, { label?: string; answer?: QuestionnaireAnswer }>;
  rsvp_questions?: {
    id: string;
    label: string;
    field_type: EventRsvpQuestion['fieldType'];
    options?: string[];
    required: boolean;
    display_order?: number;
  }[];
  viewer_user_id?: string | null;
  invited_user_ids?: string[];
  invited_user_names?: string[];
  invited_user_photo_urls?: string[];
  invite_permission?: string;

  pending_cohost_invites?: WirePendingCohostInvite[];
  my_pending_cohost_invite_id?: string | null;

  event_type?: string;
  visibility?: string;
  photo_url?: string;
  photo_updated_at?: string | null;

  tags?: WireTag[];

  is_past?: boolean;
  status?: string;
  is_partiful_import?: boolean;
  is_legacy?: boolean;
}

interface WireTag {
  id: string;
  name: string;
  slug: string;
}

interface WirePendingCohostInvite {
  id: string;
  user_id: string;
  user_name: string;
  user_photo_url?: string;
  invited_at: string;
}

function mapAttendance(value: string | undefined): AttendanceStatusValue {
  if (value === AttendanceStatus.Attended) return AttendanceStatus.Attended;
  if (value === AttendanceStatus.DidntGo) return AttendanceStatus.DidntGo;
  return AttendanceStatus.Unknown;
}

function mapQuestionnaireResponses(
  raw: Record<string, { label?: string; answer?: QuestionnaireAnswer }> | undefined,
): Event['myQuestionnaireResponses'] {
  return Object.fromEntries(
    Object.entries(raw ?? {}).map(([id, snap]) => [
      id,
      { label: snap.label ?? '', answer: snap.answer ?? '' },
    ]),
  );
}

function mapGuest(g: WireGuest): EventGuest {
  return {
    userId: g.user_id,
    name: g.name,
    status: g.status,
    phone: g.phone ?? null,
    photoUrl: g.photo_url ?? '',
    hasPlusOne: g.has_plus_one ?? false,
    attendance: mapAttendance(g.attendance),
    plusOneAttendance: mapAttendance(g.plus_one_attendance),
    isMember: g.is_member ?? true,
    paidConfirmed: g.paid_confirmed ?? false,
    paymentVerified: g.payment_verified ?? false,
    questionnaireResponses: mapQuestionnaireResponses(g.questionnaire_responses),
  };
}

function mapTag(t: WireTag): EventTag {
  return { id: t.id, name: t.name, slug: t.slug };
}

function mapPendingCohostInvite(w: WirePendingCohostInvite): PendingCohostInvite {
  return {
    id: w.id,
    userId: w.user_id,
    userName: w.user_name,
    userPhotoUrl: w.user_photo_url ?? '',
    invitedAt: new Date(w.invited_at),
  };
}

export function mapEvent(e: WireEvent): Event {
  return {
    id: e.id,
    slug: e.slug ?? '',
    title: e.title,
    description: e.description ?? '',
    startDatetime: e.start_datetime ? new Date(e.start_datetime) : null,
    endDatetime: e.end_datetime ? new Date(e.end_datetime) : null,

    location: e.location ?? '',
    latitude: e.latitude ?? null,
    longitude: e.longitude ?? null,

    whatsappLink: e.whatsapp_link ?? '',
    partifulLink: e.partiful_link ?? '',
    otherLink: e.other_link ?? '',
    venmoLink: e.venmo_link ?? '',
    cashappLink: e.cashapp_link ?? '',
    zelleInfo: e.zelle_info ?? '',
    price: e.price ?? '',

    rsvpEnabled: e.rsvp_enabled ?? false,
    allowPlusOnes: e.allow_plus_ones ?? false,
    maxAttendees: e.max_attendees ?? null,
    attendingCount: e.attending_count ?? 0,
    waitlistedCount: e.waitlisted_count ?? 0,
    invitedCount: e.invited_count ?? 0,
    guestRsvpCount: e.guest_rsvp_count ?? 0,

    datetimeTbd: e.datetime_tbd ?? false,
    hasPoll: e.has_poll ?? false,

    createdById: e.created_by_id ?? null,
    createdByName: e.created_by_name ?? null,
    createdByPhotoUrl: e.created_by_photo_url ?? '',
    coHostIds: e.co_host_ids ?? [],
    coHostNames: e.co_host_names ?? [],
    coHostPhotoUrls: e.co_host_photo_urls ?? [],

    guests: (e.guests ?? []).map(mapGuest),
    myRsvp: e.my_rsvp ?? null,
    myPaidConfirmed: e.my_paid_confirmed ?? false,
    myQuestionnaireResponses: mapQuestionnaireResponses(e.my_questionnaire_responses),
    rsvpQuestions: (e.rsvp_questions ?? []).map(mapRsvpQuestion),
    viewerUserId: e.viewer_user_id ?? null,
    invitedUserIds: e.invited_user_ids ?? [],
    invitedUserNames: e.invited_user_names ?? [],
    invitedUserPhotoUrls: e.invited_user_photo_urls ?? [],
    invitePermission: e.invite_permission ?? 'all_members',

    pendingCohostInvites: (e.pending_cohost_invites ?? []).map(mapPendingCohostInvite),
    myPendingCohostInviteId: e.my_pending_cohost_invite_id ?? null,

    eventType: e.event_type ?? 'community',
    visibility: e.visibility ?? 'public',
    photoUrl: e.photo_url ?? '',
    photoUpdatedAt: e.photo_updated_at ?? null,

    tags: (e.tags ?? []).map(mapTag),

    isPast: e.is_past ?? false,
    status: e.status ?? 'active',
    isPartifulImport: e.is_partiful_import ?? false,
    isLegacy: e.is_legacy ?? false,
  };
}

export interface WireEventGuests {
  guests?: WireGuest[];
  invited_user_ids?: string[];
  invited_user_names?: string[];
  invited_user_photo_urls?: string[];
}

export function mapEventGuests(
  w: WireEventGuests,
): Pick<Event, 'guests' | 'invitedUserIds' | 'invitedUserNames' | 'invitedUserPhotoUrls'> {
  return {
    guests: (w.guests ?? []).map(mapGuest),
    invitedUserIds: w.invited_user_ids ?? [],
    invitedUserNames: w.invited_user_names ?? [],
    invitedUserPhotoUrls: w.invited_user_photo_urls ?? [],
  };
}

/** Keep the live guest list; fill photoUrl from /guests/. Invited photos stay on event detail. */
export function mergeEventGuestPhotos(
  event: Event,
  photos: Pick<Event, 'guests'> | undefined,
): Event {
  if (!photos) return event;
  const photoById = new Map(photos.guests.map((g) => [g.userId, g.photoUrl]));
  return {
    ...event,
    guests: event.guests.map((g) => {
      const photoUrl = photoById.get(g.userId);
      return photoUrl !== undefined ? { ...g, photoUrl } : g;
    }),
  };
}
